package com.gloopgo.game.viewmodel

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.gloopgo.game.domain.ElderDetermination
import com.gloopgo.game.domain.NetworkPlayerSnapshot
import com.gloopgo.game.domain.PelletSpawnCalculator
import com.gloopgo.game.engine.EjectedMass
import com.gloopgo.game.engine.FoodPellet
import com.gloopgo.game.engine.GameConstants
import com.gloopgo.game.engine.GameEngine
import com.gloopgo.game.engine.Vector2D
import com.gloopgo.game.network.LocalMultiplayerRepository
import com.gloopgo.game.network.MultiplayerRepository
import com.gloopgo.game.audio.SoundManager
import com.gloopgo.game.audio.getSoundManager
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.launchIn
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import kotlin.time.Duration.Companion.milliseconds

/**
 * ViewModel managing the active game session, physics engine updates,
 * single-room serverless synchronization, elder cleanup, and score persistence.
 */
class GameViewModel(
    private val repository: MultiplayerRepository = LocalMultiplayerRepository(),
    private val engine: GameEngine = GameEngine(),
    private val pelletCalculator: PelletSpawnCalculator = PelletSpawnCalculator(),
    val soundManager: SoundManager = getSoundManager()
) : ViewModel() {

    private val _uiState = MutableStateFlow(GameUiState(gameState = engine.getSnapshot()))
    val uiState: StateFlow<GameUiState> = _uiState.asStateFlow()

    private var broadcastJob: Job? = null
    private var syncJob: Job? = null
    private var pelletSpawnJob: Job? = null
    private var elderCleanupJob: Job? = null
    private var isScoreSubmitted = false
    private var sessionId = ""
    private var currentEmoji = ""
    private var currentColor: Long? = null
    var sessionPeakMass: Int = 0
        private set
    private var lastArmedMass: Float = 0f
    private var isSinglePlayerSession: Boolean = false
    private var pausedAtTimestamp: Long = 0L
    private val localWorldRepository = LocalMultiplayerRepository()

    val isPaused: Boolean get() = _uiState.value.isPaused

    fun pauseGame() {
        if (!isSinglePlayerSession || _uiState.value.gameState.isGameOver || _uiState.value.isPaused) return
        pausedAtTimestamp = kotlin.time.Clock.System.now().toEpochMilliseconds()
        _uiState.value = _uiState.value.copy(isPaused = true)
    }

    fun resumeGame() {
        if (!_uiState.value.isPaused) return
        if (pausedAtTimestamp > 0L) {
            val now = kotlin.time.Clock.System.now().toEpochMilliseconds()
            val pausedDuration = now - pausedAtTimestamp
            if (pausedDuration > 0L) {
                engine.adjustTimestampsForPause(pausedDuration)
            }
            pausedAtTimestamp = 0L
        }
        _uiState.value = _uiState.value.copy(isPaused = false)
    }

    fun togglePause() {
        if (_uiState.value.isPaused) {
            resumeGame()
        } else {
            pauseGame()
        }
    }

    /** World sync/broadcast target: local in-memory for SP, Firebase for MP. */
    private fun worldRepo(): MultiplayerRepository =
        if (isSinglePlayerSession) localWorldRepository else repository

    val worldWidth: Float get() = engine.worldWidth
    val worldHeight: Float get() = engine.worldHeight

    fun initGame(
        playerName: String,
        providedPlayerId: String? = null,
        playerEmoji: String = "",
        playerColor: Long? = null,
        isSinglePlayer: Boolean = false
    ) {
        currentEmoji = playerEmoji
        currentColor = playerColor

        if (
            _uiState.value.isAuthenticated &&
            _uiState.value.playerId.isNotEmpty() &&
            engine.localPlayer != null &&
            isSinglePlayerSession == isSinglePlayer
        ) {
            return
        }

        soundManager.playGameEntrySound()
        isSinglePlayerSession = isSinglePlayer

        _uiState.value = _uiState.value.copy(
            playerName = playerName,
            isConnecting = true
        )

        sessionId = "session-${kotlin.time.Clock.System.now().toEpochMilliseconds()}"
        isScoreSubmitted = false

        viewModelScope.launch {
            try {
                if (!isSinglePlayer) {
                    repository.goOnline()
                }
                val uid = providedPlayerId
                    ?: repository.getCurrentUserId()
                    ?: repository.signInAnonymously(playerName)
                // Profile counters: SP uses sp* fields; MP uses online fields. Never touch RTDB world in SP.
                repository.recordGameJoined(uid, playerName, isSinglePlayer = isSinglePlayer)

                engine.resetWorld()
                engine.setSinglePlayerMode(isSinglePlayer)
                val localPlayer = engine.spawnLocalPlayer(playerName, uid, emoji = currentEmoji, color = currentColor)
                if (isSinglePlayer) {
                    engine.setupSinglePlayerMatch()
                }
                sessionPeakMass = localPlayer.totalMass.toInt()
                _uiState.value = _uiState.value.copy(
                    playerId = uid,
                    isAuthenticated = true,
                    isConnecting = false,
                    gameState = engine.getSnapshot()
                )

                if (!isSinglePlayer) {
                    repository.armDisconnectHandler(uid, sessionId, localPlayer.totalMass)
                    lastArmedMass = localPlayer.totalMass
                }

                startWorldSync(localPlayer.id)
                startBroadcastLoop()
                // SP: engine.replenishAmbientPelletsIfNeeded owns ambient food — no repo spawn loop.
                if (!isSinglePlayer) {
                    startPelletSpawnLoop()
                    startElderCleanupLoop()
                }
            } catch (e: Throwable) {
                _uiState.value = _uiState.value.copy(
                    isConnecting = false,
                    errorMessage = e.message ?: "Authentication failed"
                )
            }
        }
    }

    private fun startWorldSync(localPlayerId: String) {
        syncJob?.cancel()
        syncJob = viewModelScope.launch {
            val sessionStartTime = kotlin.time.Clock.System.now().toEpochMilliseconds()
            var hasRegisteredOnServer = false
            var consecutiveAbsenceCount = 0

            worldRepo().observeWorldPlayers().onEach { remoteList ->
                if (!isSinglePlayerSession) {
                    engine.syncRemotePlayers(remoteList)

                    val now = kotlin.time.Clock.System.now().toEpochMilliseconds()
                    val rawServerPlayer = remoteList.find { it.id == localPlayerId }
                    // Ignore stale ghost records from prior sessions (older than session start minus 2s tolerance)
                    val localPlayerOnServer = rawServerPlayer?.takeIf {
                        it.joinedAt >= (sessionStartTime - 2000L)
                    }

                    if (localPlayerOnServer != null && localPlayerOnServer.isAlive) {
                        hasRegisteredOnServer = true
                        consecutiveAbsenceCount = 0
                    } else if (hasRegisteredOnServer && localPlayerOnServer == null) {
                        consecutiveAbsenceCount++
                    }

                    val localInEngine = engine.localPlayer
                    if (localInEngine != null && localInEngine.isAlive) {
                        val isGracePeriodActive = (now - sessionStartTime) < 3500L
                        if (localPlayerOnServer != null && !localPlayerOnServer.isAlive && !isGracePeriodActive) {
                            engine.localPlayer = localInEngine.copy(cells = emptyList(), isAlive = false)
                            engine.isGameOver = true
                            handleDemise()
                        } else if (
                            hasRegisteredOnServer &&
                            localPlayerOnServer == null &&
                            consecutiveAbsenceCount >= 3 &&
                            !isGracePeriodActive &&
                            remoteList.isNotEmpty()
                        ) {
                            engine.localPlayer = localInEngine.copy(cells = emptyList(), isAlive = false)
                            engine.isGameOver = true
                            handleDemise()
                        }
                    }

                    // Check if local player is Elder
                    val snapshots = remoteList.map { NetworkPlayerSnapshot(it.id, it.joinedAt.toLong(), it.lastTimestamp.toLong()) }
                    val isElder = ElderDetermination.isLocalPlayerElder(localPlayerId, snapshots, now)
                    _uiState.value = _uiState.value.copy(
                        isElder = isElder,
                        gameState = engine.getSnapshot()
                    )
                } else {
                    _uiState.value = _uiState.value.copy(
                        isElder = true,
                        gameState = engine.getSnapshot()
                    )
                }
            }.launchIn(this)

            // Multiplayer only: SP pellets live in GameEngine (seed/replenish locally).
            // Applying repo snapshots in SP fights engine replenish and causes flicker.
            if (!isSinglePlayerSession) {
                worldRepo().observeWorldPellets().onEach { pelletDtos ->
                    val pellets = pelletDtos.map { dto ->
                        val pelletColor = dto.color.toLongOrNull() ?: 0xFFFFFFFFL
                        FoodPellet(
                            id = dto.id,
                            position = Vector2D(dto.x, dto.y),
                            color = pelletColor,
                            mass = dto.mass,
                            emoji = dto.emoji
                        )
                    }
                    engine.syncPellets(pellets)
                    _uiState.value = _uiState.value.copy(gameState = engine.getSnapshot())

                    // If the world is completely empty (e.g. initial launch), spawn initial shared pellets
                    if (pelletDtos.isEmpty()) {
                        val initialPellets = (0 until GameConstants.PELLET_COUNT).map {
                            val (mass, emoji, color) = pelletCalculator.generatePelletAttributes()
                            FoodPellet(
                                id = "pellet-${kotlin.time.Clock.System.now().toEpochMilliseconds()}-${kotlin.random.Random.nextInt(100000)}",
                                position = pelletCalculator.generateRandomPosition(),
                                color = color,
                                mass = mass,
                                emoji = emoji
                            )
                        }
                        worldRepo().spawnPellets(initialPellets)
                    }
                }.launchIn(this)
            }

            worldRepo().observeWorldEjected().onEach { ejectedDtos ->
                val ejectedMasses = ejectedDtos.map { dto ->
                    val massColor = dto.color.toLongOrNull() ?: 0xFFFFFFFFL
                    EjectedMass(
                        id = dto.id,
                        ownerId = dto.ownerId,
                        position = Vector2D(dto.x, dto.y),
                        velocity = Vector2D(dto.vx, dto.vy),
                        color = massColor,
                        mass = dto.mass,
                        createdAt = dto.createdAt.toLong()
                    )
                }
                engine.syncEjectedMasses(ejectedMasses)
                _uiState.value = _uiState.value.copy(gameState = engine.getSnapshot())
            }.launchIn(this)
        }
    }

    private fun startBroadcastLoop() {
        broadcastJob?.cancel()
        broadcastJob = viewModelScope.launch(Dispatchers.Default) {
            var lastSentPos = Vector2D.ZERO
            while (isActive) {
                if (!_uiState.value.isPaused) {
                    val player = engine.localPlayer
                    if (player != null && player.isAlive) {
                        val center = player.centerPosition
                        val isStationary = center.distanceSquaredTo(lastSentPos) < 1f
                        worldRepo().broadcastPlayerState(player, forceHeartbeat = isStationary)
                        lastSentPos = center

                        // Re-arm onDisconnect if mass changed by > 20% (multiplayer only)
                        if (!isSinglePlayerSession && kotlin.math.abs(player.totalMass - lastArmedMass) > 15f) {
                            lastArmedMass = player.totalMass
                            repository.armDisconnectHandler(player.id, sessionId, player.totalMass)
                        }
                    }
                }
                delay(75L.milliseconds) // ~13 Hz bandwidth-conscious update rate
            }
        }
    }

    private fun startPelletSpawnLoop() {
        pelletSpawnJob?.cancel()
        pelletSpawnJob = viewModelScope.launch(Dispatchers.Default) {
            while (isActive) {
                val currentPellets = engine.getPellets().size
                if (pelletCalculator.shouldSpawn(currentPellets)) {
                    val deficit = GameConstants.PELLET_COUNT - currentPellets
                    val batchSize = if (deficit > 30) kotlin.math.min(20, deficit) else kotlin.math.min(5, deficit)
                    val newPellets = (0 until batchSize).map {
                        val (mass, emoji, color) = pelletCalculator.generatePelletAttributes()
                        FoodPellet(
                            id = "pellet-${kotlin.time.Clock.System.now().toEpochMilliseconds()}-${kotlin.random.Random.nextInt(100000)}",
                            position = pelletCalculator.generateRandomPosition(),
                            color = color,
                            mass = mass,
                            emoji = emoji
                        )
                    }
                    worldRepo().spawnPellets(newPellets)
                }

                val playerCount = _uiState.value.gameState.remotePlayers.size + 1
                val interval = pelletCalculator.calculateSpawnInterval(currentPellets, playerCount)
                delay(interval.milliseconds)
            }
        }
    }

    private fun startElderCleanupLoop() {
        elderCleanupJob?.cancel()
        elderCleanupJob = viewModelScope.launch(Dispatchers.Default) {
            while (isActive) {
                delay(4000L.milliseconds)
                if (_uiState.value.isElder) {
                    val now = kotlin.time.Clock.System.now().toEpochMilliseconds()
                    val remotePlayers = _uiState.value.gameState.remotePlayers.map {
                        NetworkPlayerSnapshot(it.id, it.joinedAt, it.lastHeartbeat)
                    }
                    val stalePlayerIds = ElderDetermination.findStalePlayers(remotePlayers, now)
                    for (staleId in stalePlayerIds) {
                        worldRepo().pruneStalePlayer(staleId)
                    }

                    val staleMassIds = engine.getEjectedMasses().filter {
                        it.createdAt > 0L && now - it.createdAt > 60_000L
                    }.map { it.id }
                    if (staleMassIds.isNotEmpty()) {
                        worldRepo().pruneStaleEjectedMasses(staleMassIds)
                    }
                }
            }
        }
    }

    fun onGameTick(
        deltaTime: Float,
        targetWorldPos: Vector2D? = null,
        pointerOffset: Vector2D? = null,
        fallbackDirection: Vector2D = Vector2D.ZERO,
        timestamp: Long = 0L
    ) {
        if (_uiState.value.isPaused) return

        engine.update(
            deltaTime = deltaTime,
            targetWorldPos = targetWorldPos,
            pointerOffset = pointerOffset,
            fallbackDirection = fallbackDirection,
            timestamp = timestamp
        )

        val newEjected = engine.drainNewEjectedMasses()
        if (newEjected.isNotEmpty()) {
            viewModelScope.launch {
                newEjected.forEach { mass ->
                    worldRepo().broadcastEjectedMass(mass)
                }
            }
        }

        val consumedPellets = engine.drainConsumedPelletIds()
        val consumedEjected = engine.drainConsumedEjectedMassIds()
        val hazardConsumed = engine.drainHazardPelletConsumed()
        if (hazardConsumed) {
            soundManager.playHazardEatenSound()
        } else if (consumedPellets.isNotEmpty() || consumedEjected.isNotEmpty()) {
            soundManager.playPelletEatenSound()
        }

        if (consumedPellets.isNotEmpty() && !isSinglePlayerSession) {
            viewModelScope.launch {
                consumedPellets.forEach { pelletId ->
                    worldRepo().removePellet(pelletId)
                }
            }
        }

        if (consumedEjected.isNotEmpty()) {
            viewModelScope.launch {
                consumedEjected.forEach { massId ->
                    worldRepo().removeEjectedMass(massId)
                }
            }
        }

        val predations = engine.drainPredationEvents()
        if (predations.isNotEmpty()) {
            viewModelScope.launch {
                for (event in predations) {
                    worldRepo().consumePlayerCell(
                        eaterPlayerId = event.eaterPlayerId,
                        eaterCellId = event.eaterCellId,
                        victimPlayerId = event.victimPlayerId,
                        victimCellId = event.victimCellId,
                        isLastCell = event.isLastCell
                    )
                    repository.recordGameStats(
                        playerId = event.eaterPlayerId,
                        finalMass = 0,
                        killedPlayer = true,
                        isSinglePlayer = isSinglePlayerSession
                    )
                }
            }
        }

        val currentMass = engine.localPlayer?.totalMass?.toInt() ?: 0
        if (currentMass > sessionPeakMass) {
            sessionPeakMass = currentMass
        }

        val snapshot = engine.getSnapshot()
        _uiState.value = _uiState.value.copy(gameState = snapshot)

        if (snapshot.isGameOver && !isScoreSubmitted) {
            handleDemise()
        }
    }

    fun onGameTick(deltaTime: Float, movementVector: Vector2D, timestamp: Long) {
        onGameTick(
            deltaTime = deltaTime,
            targetWorldPos = null,
            fallbackDirection = movementVector,
            timestamp = timestamp
        )
    }

    private fun handleDemise() {
        if (isScoreSubmitted) return
        isScoreSubmitted = true
        pausedAtTimestamp = 0L
        broadcastJob?.cancel()
        val snapshot = engine.getSnapshot()
        val currentMass = snapshot.localPlayer?.totalMass?.toInt() ?: 0
        val finalScore = maxOf(sessionPeakMass, currentMass)
        val rank = snapshot.leaderboard.indexOfFirst { it.isLocalPlayer }.let { if (it >= 0) it + 1 else 1 }

        _uiState.value = _uiState.value.copy(
            isPaused = false,
            finalMass = finalScore,
            rank = rank,
            gameState = snapshot.copy(isGameOver = true)
        )

        viewModelScope.launch {
            val playerId = _uiState.value.playerId
            val playerName = _uiState.value.playerName
            val singlePlayer = isSinglePlayerSession
            if (!singlePlayer) {
                repository.cancelDisconnectHandler(playerId)
            }
            worldRepo().leaveWorld(playerId)
            if (finalScore > 0 && playerId.isNotEmpty()) {
                if (!singlePlayer) {
                    repository.submitScore(playerName, finalScore, playerId)
                }
                repository.recordGameStats(playerId, finalScore, isSinglePlayer = singlePlayer)
            }
        }
    }

    fun onSplit(targetWorldPos: Vector2D) {
        if (_uiState.value.isPaused) return
        val player = engine.localPlayer
        if (player != null && player.isAlive) {
            engine.splitPlayer(player.id, targetWorldPos)
            _uiState.value = _uiState.value.copy(gameState = engine.getSnapshot())
        }
    }

    fun onEjectMass(targetWorldPos: Vector2D) {
        if (_uiState.value.isPaused) return
        val player = engine.localPlayer
        if (player != null && player.isAlive) {
            engine.ejectMass(player.id, targetWorldPos)
            val newEjected = engine.drainNewEjectedMasses()
            if (newEjected.isNotEmpty()) {
                viewModelScope.launch {
                    newEjected.forEach { mass ->
                        worldRepo().broadcastEjectedMass(mass)
                    }
                }
            }
            _uiState.value = _uiState.value.copy(gameState = engine.getSnapshot())
        }
    }

    fun onRestart() {
        pausedAtTimestamp = 0L
        isScoreSubmitted = false
        engine.resetWorld()
        engine.setSinglePlayerMode(isSinglePlayerSession)
        val uid = _uiState.value.playerId
        val name = _uiState.value.playerName
        if (uid.isNotEmpty()) {
            val localPlayer = engine.spawnLocalPlayer(name, uid, emoji = currentEmoji, color = currentColor)
            if (isSinglePlayerSession) {
                engine.setupSinglePlayerMatch()
            }
            sessionPeakMass = localPlayer.totalMass.toInt()
            viewModelScope.launch {
                if (!isSinglePlayerSession) {
                    repository.armDisconnectHandler(uid, sessionId, localPlayer.totalMass)
                }
                repository.recordGameJoined(uid, name, isSinglePlayer = isSinglePlayerSession)
            }
            startWorldSync(localPlayer.id)
            startBroadcastLoop()
            if (!isSinglePlayerSession) {
                startPelletSpawnLoop()
                startElderCleanupLoop()
            }
        }
        _uiState.value = _uiState.value.copy(
            isPaused = false,
            gameState = engine.getSnapshot()
        )
    }

    fun onExit() {
        pausedAtTimestamp = 0L
        val playerId = _uiState.value.playerId
        val playerName = _uiState.value.playerName
        val currentMass = engine.localPlayer?.totalMass?.toInt() ?: 0
        val finalMass = maxOf(sessionPeakMass, currentMass)

        if (playerId.isNotEmpty() && finalMass > 0 && !isScoreSubmitted) {
            isScoreSubmitted = true
            val singlePlayer = isSinglePlayerSession
            viewModelScope.launch {
                try {
                    if (!singlePlayer) {
                        repository.cancelDisconnectHandler(playerId)
                        repository.submitScore(playerName, finalMass, playerId)
                    }
                    repository.recordGameStats(playerId, finalMass, isSinglePlayer = singlePlayer)
                } catch (e: Throwable) {
                    // Ignore on exit
                }
            }
        }

        cleanup()
        _uiState.value = _uiState.value.copy(
            isAuthenticated = false,
            isConnecting = false,
            isPaused = false
        )
    }

    private fun cleanup() {
        val playerId = _uiState.value.playerId
        broadcastJob?.cancel()
        syncJob?.cancel()
        pelletSpawnJob?.cancel()
        elderCleanupJob?.cancel()

        if (playerId.isNotEmpty()) {
            viewModelScope.launch {
                try {
                    worldRepo().leaveWorld(playerId)
                } catch (e: Throwable) {
                    // Ignore on dispose
                }
            }
        }
    }

    override fun onCleared() {
        cleanup()
        super.onCleared()
    }
}
